/**
 * Символы раздела «Сантехника» для развёртки стен: прибор спереди и, где это другой рисунок, сбоку.
 *
 * Те же правила, что у мебели: контурный рисунок в миллиметрах типового прибора, низ — пол,
 * сбоку перед смотрит вправо. Настенные приборы (умывальник, полотенцесушитель) рисуются
 * только спереди — сбоку на своей стене их не видно. Ключ реестра — id элемента каталога.
 */
import { PlumbingId } from "@/shared/catalog";
import { UIKit, type ElevationSymbols, type SymbolComponent } from "@/shared/ui";
import { handle, line, thin } from "../../elevationParts";

/** Встроенная ванна: бортик и экран до пола; сбоку — то же самое, только короче */
const BathFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 600" {...props}>
		<rect x="30" y="80" width="1640" height="510" {...line} />
		<rect x="10" y="10" width="1680" height="70" rx="20" {...line} />
		<path d="M30 540H1670" {...thin} />
	</UIKit.SymbolFrame>
);

/** Отдельностоящая ванна: чаша с бортиком на ножках */
const FreestandingBathFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 910" {...props}>
		<path d="M40 70H1560Q1530 820 1180 830H420Q70 820 40 70Z" {...line} />
		<rect x="20" y="20" width="1560" height="55" rx="25" {...line} />
		<path d="M330 820L290 900H400L430 830M1270 820L1310 900H1200L1170 830" {...thin} />
	</UIKit.SymbolFrame>
);

/** Унитаз спереди: бачок с кнопкой, под ним чаша, сужающаяся к полу */
const ToiletFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 370 800" {...props}>
		<rect x="35" y="10" width="300" height="370" rx="24" {...line} />
		<rect x="140" y="30" width="90" height="30" rx="10" {...thin} />
		<path d="M15 400H355Q355 560 290 620L270 790H100L80 620Q15 560 15 400Z" {...line} />
		<rect x="15" y="380" width="340" height="40" rx="14" {...line} />
	</UIKit.SymbolFrame>
);

/** Унитаз сбоку: бачок у стены слева, крышка и чаша вперёд, ножка к полу */
const ToiletSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 650 800" {...props}>
		<rect x="10" y="10" width="200" height="380" rx="20" {...line} />
		<path d="M150 410H620Q640 520 520 600L430 790H170Z" {...line} />
		<rect x="140" y="380" width="490" height="40" rx="14" {...line} />
	</UIKit.SymbolFrame>
);

/** Биде спереди: чаша со смесителем на бортике */
const BidetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 520 450" {...props}>
		<path d="M20 90H500Q500 250 420 300L390 440H130L100 300Q20 250 20 90Z" {...line} />
		<rect x="20" y="70" width="480" height="40" rx="14" {...line} />
		<path d="M260 70V20H320" {...line} />
	</UIKit.SymbolFrame>
);

/** Биде сбоку: чаша от стены вперёд */
const BidetSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 550 450" {...props}>
		<path d="M20 90H530Q540 230 440 300L360 440H30Z" {...line} />
		<rect x="10" y="70" width="530" height="40" rx="14" {...line} />
	</UIKit.SymbolFrame>
);

/** Стиральная (и сушильная) машина: панель с ручкой программ и дисплеем, круглый люк */
const WasherFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 850" {...props}>
		<rect x="10" y="10" width="580" height="820" rx="20" {...line} />
		<path d="M10 150H590" {...line} />
		<circle cx="490" cy="80" r="40" {...thin} />
		<rect x="60" y="55" width="220" height="50" rx="10" {...thin} />
		<circle cx="300" cy="480" r="220" {...line} />
		<circle cx="300" cy="480" r="160" {...thin} />
	</UIKit.SymbolFrame>
);

/** Душевая кабина: поддон, стеклянные створки, лейка на стойке; сбоку такая же */
const ShowerFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 2000" {...props}>
		<rect x="20" y="20" width="860" height="1820" rx="20" {...line} />
		<path d="M450 20V1840" {...thin} />
		<path d="M410 900V1100M490 900V1100" {...handle} />
		<path d="M200 260V1300M140 260H260" {...thin} />
		<rect x="10" y="1840" width="880" height="150" rx="16" {...line} />
	</UIKit.SymbolFrame>
);

/** Шкаф для ванной: две двери с ручками у стыка, цоколь */
const BathCabinetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 740 1000" {...props}>
		<rect x="10" y="10" width="720" height="920" {...line} />
		<path d="M370 10V930" {...thin} />
		<path d="M330 400V560M410 400V560" {...handle} />
		<rect x="30" y="930" width="680" height="70" {...thin} />
	</UIKit.SymbolFrame>
);

/** Пенал: высокая дверь и ящик внизу, цоколь */
const TallBathCabinetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 375 1700" {...props}>
		<rect x="10" y="10" width="355" height="1630" {...line} />
		<path d="M10 1300H365" {...thin} />
		<path d="M320 600V800M140 1470H240" {...handle} />
		<rect x="25" y="1640" width="325" height="60" {...thin} />
	</UIKit.SymbolFrame>
);

/** Умывальник с тумбой: столешница со встроенной раковиной и тумба с двумя ящиками без ручек */
const VanityFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 700" {...props}>
		<rect x="10" y="110" width="580" height="580" {...line} />
		<path d="M10 400H590" {...thin} />
		<rect x="10" y="10" width="580" height="100" rx="14" {...line} />
		<path d="M110 50H490" {...thin} strokeDasharray="24 16" />
	</UIKit.SymbolFrame>
);

/** Тумба с раковиной-чашей: чаша на столешнице, смеситель, тумба с ящиком */
const VesselVanityFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 545" {...props}>
		<rect x="10" y="230" width="580" height="305" {...line} />
		<path d="M10 270H590" {...thin} />
		<path d="M230 330H370" {...handle} />
		<path d="M140 120H460Q450 230 300 230Q150 230 140 120Z" {...line} />
		<path d="M480 230V40H400V80" {...line} />
	</UIKit.SymbolFrame>
);

/** Полотенцесушитель-лесенка: две стойки и перекладины */
const TowelDryerFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 800" {...props}>
		<rect x="30" y="10" width="40" height="780" rx="20" {...line} />
		<rect x="430" y="10" width="40" height="780" rx="20" {...line} />
		{[90, 200, 310, 420, 530, 640, 740].map((y) => (
			<path key={y} d={`M70 ${y}H430`} {...line} />
		))}
	</UIKit.SymbolFrame>
);

/** Напольная тумба с раковиной: столешница с чашей, смеситель, две дверцы и цоколь */
const FloorVanityFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 850" {...props}>
		<rect x="10" y="60" width="780" height="60" rx="10" {...line} />
		<path d="M400 60V20H460" {...line} />
		<rect x="30" y="120" width="740" height="650" {...line} />
		<path d="M400 120V770" {...thin} />
		<path d="M360 220V380M440 220V380" {...handle} />
		<rect x="50" y="770" width="700" height="70" {...thin} />
	</UIKit.SymbolFrame>
);

/** Накладная раковина-чаша на столешнице */
const VesselSinkFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 250" {...props}>
		<path d="M10 20H440Q430 240 225 240Q20 240 10 20Z" {...line} />
		<path d="M10 40H440" {...thin} />
	</UIKit.SymbolFrame>
);

/** Корзина для белья с крышкой: плетёные ряды и крышка сверху */
const HamperFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 550 440" {...props}>
		<rect x="20" y="10" width="510" height="50" rx="20" {...line} />
		<path d="M40 60H510L480 430H70Z" {...line} strokeLinejoin="round" />
		<path d="M45 160H505M55 260H495M65 350H485" {...thin} />
	</UIKit.SymbolFrame>
);

const TOILET: ElevationSymbols = { front: ToiletFront, side: ToiletSide };
const WASHER: ElevationSymbols = { front: WasherFront };

export const PLUMBING_ELEVATION_SYMBOLS: Partial<Record<PlumbingId, ElevationSymbols>> = {
	[PlumbingId.Bath]: { front: BathFront, side: BathFront },
	[PlumbingId.BathFreestanding]: { front: FreestandingBathFront, side: FreestandingBathFront },
	[PlumbingId.Toilet]: TOILET,
	[PlumbingId.Bidet]: { front: BidetFront, side: BidetSide },
	[PlumbingId.Washer]: WASHER,
	[PlumbingId.Shower]: { front: ShowerFront, side: ShowerFront },
	[PlumbingId.BathCabinet]: { front: BathCabinetFront },
	[PlumbingId.BathCabinetTall]: { front: TallBathCabinetFront },
	[PlumbingId.Pl330]: { front: VanityFront },
	[PlumbingId.VanityVessel]: { front: VesselVanityFront },
	[PlumbingId.TowelDryer]: { front: TowelDryerFront },
	[PlumbingId.ToiletModern]: TOILET,
	[PlumbingId.ToiletRound]: TOILET,
	[PlumbingId.VanityCabinetWood]: { front: FloorVanityFront },
	[PlumbingId.SinkCountertop]: { front: VesselSinkFront, side: VesselSinkFront },
	[PlumbingId.SinkCurved]: { front: VesselSinkFront, side: VesselSinkFront },
	[PlumbingId.LaundryHamper]: { front: HamperFront, side: HamperFront },
};
